import isNil from 'lodash/isNil'

export function getCpfFromCpfOrCnpjText(value: string | null | undefined) {
    if (!value) {
        return ''
    }

    if (value.length === 11) {
        return value
    }

    if (value.length === 14) {
        if (value.charAt(0) === '0' && value.charAt(1) === '0' && value.charAt(2) === '0' && value.charAt(3) === '*') {
            return value.substring(3)
        }
    }

    return ''
}

export function formatCNPJ(value: string | null | undefined) {
    if (!value) {
        return ''
    }

    if (value.length === 14) {
        // 000***527087**
        if (value.charAt(0) === '0' && value.charAt(1) === '0' && value.charAt(2) === '0' && value.charAt(3) === '*') {
            // Masked CPF
            return formatCPF(value.substring(3))
        }

        return [
            value.charAt(0),
            value.charAt(1),
            '.',
            value.charAt(2),
            value.charAt(3),
            value.charAt(4),
            '.',
            value.charAt(5),
            value.charAt(6),
            value.charAt(7),
            '/',
            value.charAt(8),
            value.charAt(9),
            value.charAt(10),
            value.charAt(11),
            '-',
            value.charAt(12),
            value.charAt(13)
        ].join('')
    }
    return value
}

export function formatCPF(value: string | null | undefined) {
    if (!value) {
        return ''
    }

    if (value.length === 11) {
        return [
            value.charAt(0),
            value.charAt(1),
            value.charAt(2),
            '.',
            value.charAt(3),
            value.charAt(4),
            value.charAt(5),
            '.',
            value.charAt(6),
            value.charAt(7),
            value.charAt(8),
            '-',
            value.charAt(9),
            value.charAt(10)
        ].join('')
    }

    return value
}

export function formatCPFOrCNPJ(value: string | null | undefined) {
    if (!value) {
        return ''
    }

    if (value.length === 11) {
        return formatCPF(value)
    }

    if (value.length === 14) {
        return formatCNPJ(value)
    }

    return value
}

export const formatTelefone = (() => {
    const regexOnlyNumberOrStar = /[^\d*]/g

    return function formatTelefone(str: string) {
        if (isNil(str)) {
            return str
        }
        str = `${str}`

        const numStr = str.replace(regexOnlyNumberOrStar, '')

        if (numStr.length === 10) {
            return `(${numStr.substring(0, 2)}) ${numStr.substring(2, 6)}-${numStr.substring(6)}`
        }

        if (numStr.length === 12) {
            return `+${numStr.substring(0, 2)} (${numStr.substring(2, 4)}) ${numStr.substring(4, 8)}-${numStr.substring(
                8
            )}`
        }

        if (numStr.length === 11) {
            return `(${numStr.substring(0, 2)}) ${numStr.substring(2, 7)}-${numStr.substring(7)}`
        }

        if (numStr.length === 13) {
            return `+${numStr.substring(0, 2)} (${numStr.substring(2, 4)}) ${numStr.substring(4, 9)}-${numStr.substring(
                9
            )}`
        }

        return str
    }
})()

export function formatCRM(value: unknown) {
    if (typeof value !== 'string' || value.length < 3) {
        return
    }
    const slipPos = value.length - 2
    return `${value.substring(0, slipPos)}/${value.substring(slipPos)}`
}

export function formatNumber(value: number) {
    return value.toLocaleString('pt-BR', { style: 'decimal' })
}

export function formatCurrency(value: number) {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function parseNonNegativeDecimal(text: string): number | undefined {
    const normalized = text.trim().replace(',', '.')

    if (!normalized) {
        return undefined
    }

    const parsed = Number(normalized)
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined
}

export function parseLocalDateTimeNoTZ(dateTimeStr: string): Date {
    const [datePart, timePart] = dateTimeStr.split('T')
    const [year, month, day] = datePart.split('-').map(Number)
    const [hour, minute] = timePart.split(':').map(Number)

    return new Date(year, month - 1, day, hour, minute)
}

export function parseLocalDateNoTZ(dateTimeStr: string): Date {
    const [datePart] = dateTimeStr.split('T')
    const [year, month, day] = datePart.split('-').map(Number)

    return new Date(year, month - 1, day)
}

const MONTH_ABBR = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

export function formatMonthLabel(period: string): string {
    const [year, month] = period.split('-')
    const index = Number(month) - 1

    if (!year || Number.isNaN(index) || index < 0 || index > 11) {
        return period
    }

    return `${MONTH_ABBR[index]}/${year.slice(2)}`
}

export function competenciaKey(value?: string | null): string | undefined {
    const match = /^(\d{4})-(\d{2})/.exec(value ?? '')
    return match ? `${match[1]}-${match[2]}` : undefined
}

export function formatCompetencia(value?: string | null): string {
    const key = competenciaKey(value)
    return key ? formatMonthLabel(key) : '—'
}

export function formatDayLabel(period: string): string {
    const [, month, day] = period.split('-')
    return month && day ? `${day}/${month}` : period
}

export function formatDateNoTz(dateVal: string | Date): string {
    if (!dateVal) return ''

    const date = new Date(dateVal)

    return new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'UTC',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(date)
}
